import { isValidElement } from 'react';

import {
  TailwindHeightClass,
  TailwindMaxWidthClass,
  TailwindWidthClass
} from '../../../utils/tailwind.types';
import { cn } from '../../../utils/tailwind.utils';
import { Filters, FiltersProps } from '../../shared/Filters';
import { Button, ButtonType } from '../Button';
import { Size, TextIconColor } from '../constants';
import {
  DateTimeRangePicker,
  DateTimeRangePickerProps
} from '../DateTimeRangePicker';
import { withLink } from '../hocs';
import { Icon, Icons } from '../Icon';
import { LoadingSpinner, LoadingSpinnerSize } from '../LoadingSpinner';
import SortHeader, {
  CsvHeader,
  Header,
  TableSort
} from '../SortHeader/SortHeader';
import { TabNavigator } from '../TabNavigator';
import { TabNavigatorType } from '../TabNavigator/TabNavigator.consts';
import { TabOption } from '../TabNavigator/TabNavigatorOption';
import { ToolTip } from '../ToolTip';
import { TooltipPlacement } from '../ToolTip/ToolTip';
import {
  Typography,
  TypographyCasing,
  TypographyFont,
  TypographySize,
  TypographyWeight,
  TypographyWrap
} from '../Typography';

import { tableStyles } from './Table.styles';

const DEFAULT_ROW_HEIGHT = 'h-16';

const getAlignmentClass = (align?: 'left' | 'center' | 'right'): string => {
  switch (align) {
    case 'center':
      return 'justify-center';
    case 'right':
      return 'justify-end';
    case 'left':
    case undefined:
    default:
      return 'justify-start';
  }
};

function renderTableHeader<SortKey extends string>(
  col: TableColumn<SortKey>,
  sortProps?: {
    activeSort: TableSort<SortKey>;
    setActiveSort: (value: TableSort<SortKey>) => void;
  },
  isCsv = false
) {
  const canSort = !!sortProps && !!col.sortKey;

  if (canSort) {
    return (
      <SortHeader<SortKey>
        activeSort={sortProps.activeSort}
        color={TextIconColor.SECONDARY}
        setSortKey={(newKey) => sortProps.setActiveSort(newKey)}
        sortKey={col.sortKey as SortKey}
        title={col.title}
      />
    );
  }
  if (isCsv) {
    return <CsvHeader title={col.title} />;
  }
  return <Header color={TextIconColor.SECONDARY} title={col.title} />;
}

export interface TableColumn<SortKey extends string = string> {
  title: string;
  align?: 'left' | 'center' | 'right';
  sortKey?: SortKey | undefined;
  tooltipEnabled?: boolean;
  width?: TailwindWidthClass;
  wrap?: TypographyWrap;
}

export interface TableRow<TRowKey extends string = string> {
  cells: React.ReactNode[];
  key: TRowKey;
  disabled?: boolean;
  disabledTextColor?: TextIconColor | undefined;
  height?: TailwindHeightClass;
  href?: string;
  onClick?: () => void;
  textColor?: TextIconColor;
  toolTip?: string | undefined;
}

export interface TableSelectionProps<TRowKey> {
  selectedItems: TRowKey[];
  setSelectedItems: (keys: TRowKey[]) => void;
  singleSelection?: boolean;
}

interface ClearFilterProps {
  hasFilters: boolean;
  label: string;
  onClick: () => void;
}

export interface TabViewProps<TabValue extends string> {
  activeTab: TabValue;
  setActiveTab: (tab: TabValue) => void;
  tabOptions: TabOption<TabValue>[];
}

export type TableProps<
  TRowKey extends string,
  FilterKey extends string,
  SortKey extends string,
  TabValue extends string
> = {
  columns: TableColumn<SortKey>[];
  noRowsText: string;
  rows: (TableRow<TRowKey> | React.ReactElement)[];
  clearFilterProps?: ClearFilterProps;
  dateRangePickerProps?: DateTimeRangePickerProps;
  filterProps?: FiltersProps<FilterKey> | undefined;
  isCsv?: boolean;
  loading?: boolean;
  selectionProps?: TableSelectionProps<TRowKey>;
  showColumnHeaderBorder?: boolean;
  showHeader?: boolean;
  sortProps?:
    | {
        activeSort: TableSort<SortKey>;
        setActiveSort: (value: TableSort<SortKey>) => void;
      }
    | undefined;
  tabViewProps?: TabViewProps<TabValue> | undefined;
  title?: string;
};

function Table<
  TKey extends string,
  FilterKey extends string = string,
  SortKey extends string = string,
  TabValue extends string = string
>({
  columns,
  rows,
  selectionProps,
  noRowsText,
  clearFilterProps,
  dateRangePickerProps,
  filterProps,
  loading = false,
  showColumnHeaderBorder = false,
  sortProps,
  title,
  showHeader = true,
  tabViewProps,
  isCsv = false
}: TableProps<TKey, FilterKey, SortKey, TabValue>) {
  const { selectedItems, setSelectedItems, singleSelection } =
    selectionProps ?? {};

  const handleToggleSelection = (key: TKey) => {
    if (!setSelectedItems || !selectedItems) return;

    if (selectedItems?.includes(key)) {
      setSelectedItems(selectedItems.filter((item) => item !== key));
    } else {
      setSelectedItems(singleSelection ? [key] : [...selectedItems, key]);
    }
  };

  // Leave space in the header for checkboxes if selection is enabled
  const columnsWithSelect: TableColumn<SortKey>[] = selectionProps
    ? [
        {
          title: '',
          sortKey: undefined,
          width: 'w-1/12'
        },
        ...columns
      ]
    : columns;

  const showFilters = filterProps || dateRangePickerProps;

  const { tableCellFont, tableCellSize } = isCsv
    ? {
        tableCellFont: TypographyFont.PARAGRAPH,
        tableCellSize: TypographySize.MEDIUM
      }
    : {
        tableCellFont: TypographyFont.PARAGRAPH,
        tableCellSize: TypographySize.SMALL
      };

  const styles = tableStyles({
    showHeaderBorder: showColumnHeaderBorder,
    hideBorder: isCsv
  });

  return (
    <div className={styles.container()} data-testid="table-container">
      {title && (
        <Typography
          casing={TypographyCasing.CAPITALIZE}
          color={TextIconColor.PRIMARY}
          font={TypographyFont.HEADING}
          size={TypographySize.H4}
          weight={TypographyWeight.SEMI_BOLD}
        >
          {title}
        </Typography>
      )}
      {(tabViewProps || showFilters) && (
        <div
          className="flex flex-col gap-4"
          data-testid="table-tabs-filters-container"
        >
          {tabViewProps && (
            <TabNavigator
              activeTab={tabViewProps.activeTab}
              onClick={tabViewProps.setActiveTab}
              orientation={TabNavigatorType.HORIZONTAL}
              size={Size.SMALL}
              tabOptions={tabViewProps.tabOptions}
            />
          )}
          {showFilters && (
            <div className="flex gap-2" data-testid="table-filter-container">
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              {filterProps && (
                <Filters {...{ ...filterProps, children: undefined }} />
              )}
              {dateRangePickerProps && (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <DateTimeRangePicker {...dateRangePickerProps} />
              )}
              {filterProps?.children}
              {clearFilterProps?.hasFilters && (
                <div
                  className="flex flex-1 justify-end"
                  data-testid="table-clear-filter-button-container"
                >
                  <Button
                    label={clearFilterProps.label}
                    onClick={clearFilterProps.onClick}
                    type={ButtonType.OUTLINED}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <table className={styles.root()} data-testid="table-root">
        {showHeader && (
          <thead className={styles.head()} data-testid="table-head">
            <tr className={styles.headerRow()} data-testid="table-header-row">
              {columnsWithSelect.map((col) => (
                <th
                  className={cn(styles.headerCell(), col.width)}
                  data-testid="table-header-cell"
                  key={col.title}
                >
                  {renderTableHeader(col, sortProps, isCsv)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row) => {
            if (isValidElement(row)) return row;

            const tableRow = row;
            const {
              key,
              disabled,
              disabledTextColor,
              toolTip,
              height = DEFAULT_ROW_HEIGHT,
              onClick: customOnClick,
              href,
              textColor = TextIconColor.PRIMARY
            } = tableRow;

            const rowKey = key;
            const isSelected = selectedItems?.includes(rowKey) ?? false;
            const isDisabled = disabled ?? false;

            const toggleRowSelectionIfEnabled = () => {
              if (disabled) return;
              handleToggleSelection(rowKey);
            };

            const handleRowClick = () => {
              if (isDisabled) return;

              if (customOnClick) {
                customOnClick();
              }

              toggleRowSelectionIfEnabled();
            };

            const rowIsClickable =
              !!selectionProps || !!customOnClick || !!href;

            const rowTextColor = disabled
              ? disabledTextColor || TextIconColor.DISABLED
              : textColor;

            const rowStyles = tableStyles({
              clickable: rowIsClickable,
              disabled: isDisabled,
              hideBorder: isCsv,
              isSelected
            });

            const heightClass = height || DEFAULT_ROW_HEIGHT;

            return (
              <ToolTip content={toolTip} key={row.key}>
                <tr
                  className={cn(rowStyles.row(), heightClass)}
                  data-row-key={row.key}
                  data-testid="table-row"
                  onClick={handleRowClick}
                >
                  {selectionProps && (
                    <td
                      className={rowStyles.checkboxTd()}
                      data-row-key={row.key}
                      data-testid="table-checkbox-td"
                      onClick={toggleRowSelectionIfEnabled}
                    >
                      <div
                        className={rowStyles.checkboxOuterContainer()}
                        data-testid="table-checkbox-outer-container"
                      >
                        {isSelected && (
                          <div
                            className={rowStyles.checkboxInnerContainer()}
                            data-testid="table-checkbox-inner-container"
                          >
                            <Icons
                              color={TextIconColor.BLACK}
                              icon={Icon.CHECK}
                              size={Size.MEDIUM}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {tableRow.cells.map((cell, index) => {
                    const column = columns[index];
                    const { width, wrap, tooltipEnabled, align } = column ?? {};
                    const alignmentClass = getAlignmentClass(align);

                    return (
                      <td
                        // eslint-disable-next-line react/no-array-index-key
                        className={cn(styles.cell(), heightClass, width)}
                        data-cell-index={index}
                        data-row-key={row.key}
                        data-testid="table-cell"
                        key={`row-${row.key}-cell-${index}`}
                      >
                        {withLink(
                          <>
                            {isValidElement(cell) && (
                              <div
                                className={cn(
                                  styles.cellContent(),
                                  alignmentClass
                                )}
                                data-testid="table-cell-content"
                              >
                                {cell}
                              </div>
                            )}
                            {!isValidElement(cell) && (
                              <ToolTip
                                content={typeof cell === 'string' ? cell : ''}
                                disabled={!tooltipEnabled}
                                placement={TooltipPlacement.TOP_START}
                              >
                                <div
                                  className={cn(
                                    styles.cellContent(),
                                    alignmentClass
                                  )}
                                  data-testid="table-cell-content"
                                >
                                  <Typography
                                    color={rowTextColor}
                                    font={tableCellFont}
                                    maxWidth={
                                      width
                                        ? // TODO: Figure out cleaner way to do this, cast
                                          // only required because width accepts w-auto and max width doesn't
                                          (`max-${width}` as TailwindMaxWidthClass)
                                        : undefined
                                    }
                                    size={tableCellSize}
                                    wrap={wrap}
                                  >
                                    {cell}
                                  </Typography>
                                </div>
                              </ToolTip>
                            )}
                          </>,
                          { href, fullSize: true }
                        )}
                      </td>
                    );
                  })}
                </tr>
              </ToolTip>
            );
          })}
          {!rows.length && !loading && (
            <tr data-testid="table-no-rows-row">
              <td
                className={styles.noRowsTd()}
                colSpan={columnsWithSelect.length}
                data-testid="table-no-rows-td"
              >
                <Typography color={TextIconColor.SECONDARY} italic>
                  {noRowsText}
                </Typography>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {loading && <LoadingSpinner size={LoadingSpinnerSize.LARGE} />}
    </div>
  );
}

export default Table;
